import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Image } from "@/components/ui/image";
import { Download, X, Smartphone, Monitor, Tv, Check, Package, TrendingDown, Sparkles, Share, Plus, MoreVertical } from "lucide-react";

const LOGO_URL = "https://media.base44.com/images/public/6aa6211001d513cafb0a3a5c/8b0969465_BHGC.png";

const PERKS = [
  "Cotiza +30 paqueterías en segundos",
  "Ahorra hasta 40% en cada envío",
  "IA transcribe tus envíos automáticamente",
  "Rastreo centralizado en tiempo real",
  "Sin contratos, sin mínimos, sin letras chiquitas"
];

// Detecta navegador + plataforma para mostrar las instrucciones de
// instalación correctas. beforeinstallprompt dispara en navegadores basados
// en Chromium (Chrome, Edge, Brave, Opera, Vivaldi, Samsung Internet,
// Arc); iOS/Safari, Firefox Android y Safari macOS requieren pasos manuales.
function detectPlatform() {
  const ua = navigator.userAgent || "";
  const mobile = /android|iphone|ipad|ipod/i.test(ua);
  // iOS: Safari nativo (Chrome/Firefox en iOS usan el motor Safari → mismo flujo).
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // ===== Smart TV / TV Box: no soportan instalación de PWA; guiar al móvil/PC.
  if (/web0s|netcast/i.test(ua)) return "tv-webos";            // LG WebOS
  if (/googletv/i.test(ua)) return "tv-google";               // Google TV / Smart TV
  if (/android/i.test(ua) && / tv|tvbox| tv |smarttv|aft|bravia|mitv|hisense|\btcl\b|philips|shield/i.test(ua)) return "tv-android"; // Android TV / TV Box
  if (/android/i.test(ua)) {
    if (/samsungbrowser/i.test(ua)) return "android-samsung";
    if (/firefox|fxios/i.test(ua)) return "android-firefox";
    if (/opr\/|opera/i.test(ua)) return "android-opera";
    // Chrome, Edge mobile, Brave, Vivaldi (Chromium → beforeinstallprompt).
    return "android-chrome";
  }
  if (!mobile) {
    // Safari macOS: sin beforeinstallprompt (Agregar al Dock en macOS 14+).
    if (/safari/i.test(ua) && !/chrome|chromium|edg|opr|vivaldi|brave/i.test(ua)) return "desktop-safari";
    // Firefox escritorio: sin soporte de PWA (solo marcador).
    if (/firefox/i.test(ua) && !/seamonkey/i.test(ua)) return "desktop-firefox";
    // Chromium escritorio: Chrome, Edge, Brave, Opera, Vivaldi, Arc.
    if (/chrome|chromium|edg|opr|vivaldi|brave/i.test(ua)) return "desktop-chromium";
    return "desktop-safari"; // fallback genérico de escritorio.
  }
  return "other";
}

export default function DownloadAppModal({ open, onClose }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (!open) return;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.matchMedia("(display-mode: window-controls-overlay)").matches
      || window.navigator.standalone === true;
    setInstalled(isStandalone);
    setPlatform(detectPlatform());
    setShowInstructions(false);

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    // appinstalled: limpia el prompt diferido si el usuario instala desde fuera.
    const onInstalled = () => { setDeferredPrompt(null); setInstalled(true); };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [open]);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") setInstalled(true);
    } catch { /* ignore */ }
    setDeferredPrompt(null);
  };

  // Un solo botón "Instalar ahora": si el navegador disparó beforeinstallprompt
  // (Chrome/Edge/Android), lanza el prompt nativo; si no, revela las
  // instrucciones de instalación manual para la plataforma detectada.
  const onInstallNow = () => {
    if (deferredPrompt) install();
    else setShowInstructions(true);
  };

  const platformLabel = {
    "ios": "iPhone / iPad · Safari",
    "android-chrome": "Android · Chrome/Edge/Brave",
    "android-samsung": "Android · Samsung Internet",
    "android-firefox": "Android · Firefox",
    "android-opera": "Android · Opera",
    "tv-webos": "LG WebOS · TV",
    "tv-google": "Google TV / Smart TV",
    "tv-android": "Android TV / TV Box",
    "desktop-chromium": "Escritorio · Chrome/Edge/Brave/Opera",
    "desktop-safari": "Escritorio · Safari (macOS)",
    "desktop-firefox": "Escritorio · Firefox",
    "other": "Tu navegador",
  }[platform] || "Tu navegador";

  const isMobile = ["ios", "android-chrome", "android-samsung", "android-firefox", "android-opera"].includes(platform);
  const isTv = ["tv-webos", "tv-google", "tv-android"].includes(platform);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/85 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ duration: 0.28 }}
            onClick={e => e.stopPropagation()}
            className="relative w-full max-w-md surface border-cyan/30 glow-cyan max-h-[90dvh] overflow-y-auto"
          >
            <button onClick={onClose} className="absolute top-3 right-3 text-ink-muted hover:text-ink-text z-10">
              <X className="w-4 h-4" />
            </button>

            <div className="p-6 text-center border-b border-ink-border">
              <div className="inline-flex relative mb-4">
                <div className="absolute inset-0 blur-xl bg-cyan/40 rounded-full" />
                <div className="relative w-16 h-16 flex items-center justify-center bg-ink border border-cyan/40">
                  <Image src={LOGO_URL} alt="BHGC Nexus" className="w-11 h-11" fittingType="fill" />
                </div>
              </div>
              <h2 className="font-display font-bold text-lg uppercase tracking-wide">BHGC Nexus Logistics</h2>
              <p className="text-xs text-ink-muted mt-1">App web instalable (PWA) · Gratis · {platformLabel}</p>
            </div>

            <div className="p-6">
              {installed ? (
                <div className="flex items-center gap-2.5 p-3 surface-inset border-success/40 text-sm text-success mb-5">
                  <Check className="w-4 h-4 shrink-0" /> Ya tienes la app instalada. Ábrela desde tu pantalla de inicio.
                </div>
              ) : (
                <ul className="space-y-2.5 mb-5">
                  {PERKS.map(p => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-ink-text">
                      <Check className="w-4 h-4 text-success mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              )}

              {!installed && (
                <>
                  {/* Botón único "Instalar ahora": autodetecta dispositivo/navegador.
                      Si el navegador disparó beforeinstallprompt (Chrome/Edge/Android),
                      lanza el prompt nativo de instalación. Si no, revela las
                      instrucciones manuales para la plataforma detectada (iOS,
                      Android o escritorio). */}
                  <button
                    onClick={onInstallNow}
                    className="w-full flex items-center justify-center gap-2 bg-cyan hover:bg-cyan-hover text-ink px-5 py-3 font-display font-bold uppercase tracking-wide glow-cyan transition-colors mb-3"
                  >
                    {deferredPrompt ? <Download className="w-4 h-4" /> : isTv ? <Tv className="w-4 h-4" /> : isMobile ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
                    Instalar ahora
                  </button>
                  {deferredPrompt && (
                    <p className="text-[0.7rem] text-ink-muted/80 text-center mb-3">
                      Tu navegador mostrará el diálogo de instalación. Acéptalo para agregar el ícono a tu inicio/dock.
                    </p>
                  )}

                  {/* Instrucciones manuales por navegador/plataforma: se muestran
                      cuando beforeinstallprompt no está disponible (iOS/Safari,
                      Firefox Android, Safari macOS, Firefox escritorio) o cuando
                      el usuario toca el botón sin prompt nativo. */}
                  {(showInstructions || !deferredPrompt) && (
                    <>
                      {platform === "ios" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Smartphone className="w-3.5 h-3.5" /> Instalar en iPhone/iPad (Safari)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li className="flex items-center gap-1.5"><Share className="w-3 h-3 text-cyan shrink-0" /> Toca <strong className="text-ink-text">Compartir</strong> en Safari.</li>
                            <li>Selecciona <strong className="text-ink-text">Agregar a pantalla de inicio</strong>.</li>
                            <li className="flex items-center gap-1.5"><Plus className="w-3 h-3 text-cyan shrink-0" /> Toca <strong className="text-ink-text">Agregar</strong> y ábrela desde tu inicio.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "android-chrome" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Smartphone className="w-3.5 h-3.5" /> Instalar en Android (Chrome/Edge/Brave)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li className="flex items-center gap-1.5"><MoreVertical className="w-3 h-3 text-cyan shrink-0" /> Toca el menú <strong className="text-ink-text">⋮</strong> arriba a la derecha.</li>
                            <li>Selecciona <strong className="text-ink-text">Agregar a pantalla de inicio</strong> / <strong className="text-ink-text">Instalar app</strong>.</li>
                            <li>Ábrela desde tu inicio como una app nativa.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "android-samsung" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Smartphone className="w-3.5 h-3.5" /> Instalar en Android (Samsung Internet)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Toca el menú <strong className="text-ink-text">≡</strong> (líneas) abajo.</li>
                            <li>Selecciona <strong className="text-ink-text">Añadir a página de inicio</strong> o <strong className="text-ink-text">Instalar aplicación</strong>.</li>
                            <li>Confirma y ábrela desde tu pantalla de inicio.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "android-firefox" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Smartphone className="w-3.5 h-3.5" /> Instalar en Android (Firefox)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li className="flex items-center gap-1.5"><MoreVertical className="w-3 h-3 text-cyan shrink-0" /> Toca el menú <strong className="text-ink-text">⋮</strong>.</li>
                            <li>Selecciona <strong className="text-ink-text">Agregar a pantalla de inicio</strong>.</li>
                            <li>Confirma <strong className="text-ink-text">Agregar</strong> y ábrela desde tu inicio.</li>
                          </ol>
                          <div className="mt-2.5 text-[0.7rem] text-ink-muted/80">
                            Firefox Android no soporta instalación automática, pero crea un acceso que abre la app a pantalla completa.
                          </div>
                        </div>
                      )}
                      {platform === "android-opera" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Smartphone className="w-3.5 h-3.5" /> Instalar en Android (Opera)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li className="flex items-center gap-1.5"><MoreVertical className="w-3 h-3 text-cyan shrink-0" /> Toca el menú <strong className="text-ink-text">⋮</strong> abajo.</li>
                            <li>Selecciona <strong className="text-ink-text">Añadir a pantalla de inicio</strong>.</li>
                            <li>Ábrela desde tu inicio como una app.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "tv-webos" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Tv className="w-3.5 h-3.5" /> LG WebOS (Smart TV)
                          </div>
                          <p className="text-xs text-ink-muted mb-2">
                            El navegador nativo de LG WebOS no permite instalar PWAs ni agregar íconos a pantalla de inicio.
                          </p>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Abre BHGC Nexus en tu <strong className="text-ink-text">teléfono o computadora</strong> para instalar la app.</li>
                            <li>En la TV, puedes usarla abriendo <strong className="text-ink-text">bhgcnexuslogistics.com</strong> en el navegador nativo como acceso directo.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "tv-google" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Tv className="w-3.5 h-3.5" /> Google TV / Smart TV (Android TV de Google)
                          </div>
                          <p className="text-xs text-ink-muted mb-2">
                            Google TV no soporta instalación de PWAs desde su navegador nativo.
                          </p>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Instala la app en tu <strong className="text-ink-text">móvil o PC</strong> (Chrome/Edge/Safari).</li>
                            <li>Si tu TV tiene Chrome o un navegador Android, abre <strong className="text-ink-text">bhgcnexuslogistics.com</strong> y úsala como acceso.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "tv-android" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Tv className="w-3.5 h-3.5" /> Android TV / TV Box
                          </div>
                          <p className="text-xs text-ink-muted mb-2">
                            Tu TV Box o Android TV no está pensada para instalar PWAs (la navegación con control remoto no es ideal).
                          </p>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Instala la app en tu <strong className="text-ink-text">teléfono o computadora</strong> para una mejor experiencia.</li>
                            <li>Si tu TV tiene Chrome, abre el menú <strong className="text-ink-text">⋮ → Agregar a pantalla de inicio</strong> para un acceso (navegación con control remoto).</li>
                          </ol>
                        </div>
                      )}
                      {platform === "desktop-chromium" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Monitor className="w-3.5 h-3.5" /> Instalar en escritorio (Chrome/Edge/Brave/Opera/Vivaldi)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Toca el ícono <strong className="text-ink-text">⊕ Instalar</strong> al final de la barra de direcciones.</li>
                            <li>O abre el menú <strong className="text-ink-text">⋮</strong> / <strong className="text-ink-text">⋯</strong> → <strong className="text-ink-text">Instalar BHGC Nexus</strong> / <strong className="text-ink-text">Instalar página como app</strong>.</li>
                            <li>Confirma <strong className="text-ink-text">Instalar</strong>. Se abre en su propia ventana como una app de escritorio.</li>
                          </ol>
                        </div>
                      )}
                      {platform === "desktop-safari" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Monitor className="w-3.5 h-3.5" /> Instalar en escritorio (Safari · macOS 14+)
                          </div>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Abre el menú <strong className="text-ink-text">Archivo</strong> en la barra de menús.</li>
                            <li>Selecciona <strong className="text-ink-text">Agregar al Dock</strong>.</li>
                            <li>Confirma y ábrela desde el Dock como una app.</li>
                          </ol>
                          <div className="mt-2.5 text-[0.7rem] text-ink-muted/80">
                            Safari macOS no muestra un botón de instalación automática; usa el menú Archivo.
                          </div>
                        </div>
                      )}
                      {platform === "desktop-firefox" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Monitor className="w-3.5 h-3.5" /> Firefox escritorio
                          </div>
                          <p className="text-xs text-ink-muted mb-2">
                            Firefox de escritorio no soporta instalar PWAs. Puedes crear un acceso directo:
                          </p>
                          <ol className="space-y-1.5 text-xs text-ink-muted list-decimal list-inside">
                            <li>Arrastra el candado de la barra de direcciones al escritorio/escritorio.</li>
                            <li>O marca la página (<strong className="text-ink-text">Ctrl/⌘ + D</strong>) y ábrela cuando la necesites.</li>
                          </ol>
                          <div className="mt-2.5 text-[0.7rem] text-ink-muted/80">
                            Para la experiencia de app instalable, abre esta página en Chrome, Edge o Brave.
                          </div>
                        </div>
                      )}
                      {platform === "other" && (
                        <div className="surface-inset p-4 mb-4 text-left">
                          <div className="text-xs font-display font-bold uppercase text-cyan mb-2 flex items-center gap-1.5">
                            <Monitor className="w-3.5 h-3.5" /> Instalar la app web
                          </div>
                          <p className="text-xs text-ink-muted">
                            Busca la opción <strong className="text-ink-text">"Instalar app"</strong> o <strong className="text-ink-text">"Agregar a pantalla de inicio / al Dock"</strong> en el menú de tu navegador.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <div className="grid grid-cols-3 gap-2 mb-5">
                <div className="surface-inset p-3 text-center">
                  <Package className="w-4 h-4 text-cyan mx-auto mb-1" />
                  <div className="text-[0.65rem] text-ink-muted">Cotiza</div>
                </div>
                <div className="surface-inset p-3 text-center">
                  <Sparkles className="w-4 h-4 text-cyan mx-auto mb-1" />
                  <div className="text-[0.65rem] text-ink-muted">IA</div>
                </div>
                <div className="surface-inset p-3 text-center">
                  <TrendingDown className="w-4 h-4 text-cyan mx-auto mb-1" />
                  <div className="text-[0.65rem] text-ink-muted">Ahorra 40%</div>
                </div>
              </div>

            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}